import { useCallback, useMemo } from 'react';
import { SWRConfiguration } from 'swr';

import { useLoggedInEmployeeId, useLoggedInUserRoles } from '~/components/Auth';
import { useMyUnits } from '~/data/orgunits';
import { RelationshipType } from '~/data/search';
import { Roles } from '~/data/user';
import { useGetRoleResponsibilityOverEmployee } from '~/hooks/employee/useGetRoleResponsibilityOverEmployee';
import { hasOneOfRoles } from '~/services/Permissions';

type PropsType = {
  employeeId?: number;
  unitId?: number;
};

type HasRoleAccessCheck = (
  oneOfRoles: Roles[],
  withoutRoles?: Roles[],
) => boolean;

export type UseMyAccessToSelectedEmployeeOutput = {
  isLoading: boolean;
  isInMyUnits: boolean;
  isMyselfWithRole: HasRoleAccessCheck;
  managerRelationshipType?: RelationshipType;
  hasManagerRelationshipType: (
    expectedRelationships: RelationshipType[],
  ) => boolean;
  accessRolesOverEmployee: Roles[];
  hasRoleAccessOverEmployee: HasRoleAccessCheck;
};

export const useMyAccessToSelectedEmployee = (
  props: PropsType,
  swrConfig?: SWRConfiguration,
): UseMyAccessToSelectedEmployeeOutput => {
  const { employeeId, unitId } = props;

  const loggedInEmployeeId = useLoggedInEmployeeId();

  // Responsibility roles
  const myRoles = useLoggedInUserRoles();
  const { responsibilityRoles, managerRelationshipType, isLoading } =
    useGetRoleResponsibilityOverEmployee(
      {
        roles: myRoles,
        responsibleEmployeeId: loggedInEmployeeId,
        employeeId: employeeId,
      },
      swrConfig,
    );

  const hasRoleAccess = useCallback(
    (roles: Roles[]): HasRoleAccessCheck =>
      (oneOfRoles: Roles[], withoutRoles?: Roles[]) => {
        // Ensure expected roles are not part of reject condition
        const rejectWithRoles = !!withoutRoles
          ? withoutRoles.filter((r) => !oneOfRoles.includes(r))
          : undefined;

        return (
          hasOneOfRoles(roles, oneOfRoles) &&
          (!!rejectWithRoles ? !hasOneOfRoles(roles, rejectWithRoles) : true)
        );
      },
    [],
  );

  const { isMyselfWithRole, hasRoleAccessOverEmployee } = useMemo(() => {
    const isMyselfWithRole: HasRoleAccessCheck =
      loggedInEmployeeId === employeeId
        ? hasRoleAccess(myRoles as Roles[])
        : () => {
            return false;
          };

    return {
      isMyselfWithRole,
      hasRoleAccessOverEmployee: hasRoleAccess(responsibilityRoles),
    };
  }, [
    loggedInEmployeeId,
    employeeId,
    hasRoleAccess,
    myRoles,
    responsibilityRoles,
  ]);

  // My Units
  const { myUnits } = useMyUnits(true);

  const { isInMyUnits } = useMemo(() => {
    return {
      isInMyUnits:
        !!unitId && myUnits.some((orgUnit) => orgUnit.fOrgUnitId === unitId),
    };
  }, [unitId, myUnits]);

  const hasManagerRelationshipType = useCallback(
    (expectedRelationships: RelationshipType[]) =>
      !!managerRelationshipType &&
      expectedRelationships.includes(managerRelationshipType),
    [managerRelationshipType],
  );

  return {
    isLoading,
    isInMyUnits,
    isMyselfWithRole,
    managerRelationshipType,
    accessRolesOverEmployee: responsibilityRoles,
    hasRoleAccessOverEmployee,
    hasManagerRelationshipType,
  };
};
